import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  MenuItem,
  Tabs,
  Tab
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
// import { useNavigate } from 'react-router-dom';

// Using address shape from AuthContext (state, zipCode). No local override.

const ProfilePage = () => {
  const { user, login, register, logout, updateUser, addAddress, deleteAddress, deleteRecipient, updateRecipient } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: ''
  });
  const [phonePrefix, setPhonePrefix] = useState('+52');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [addressDialog, setAddressDialog] = useState(false);
  const [addressForm, setAddressForm] = useState({
    street: '',
    colonia: '',
    city: '',
    region: '',
    postalCode: ''
  });
  const [orders, setOrders] = useState<any[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersError, setOrdersError] = useState('');

  // Handle navigation state for tab selection
  useEffect(() => {
    const state = location.state as { tab?: number } | null;
    if (state && typeof state.tab === 'number') {
      setTabIndex(state.tab);
      // Clear state to avoid persistent tab selection on reload if desired, 
      // but strictly speaking not necessary unless it causes issues.
      // navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  const [openEditRecipientDialog, setOpenEditRecipientDialog] = useState(false);
  const [editRecipientForm, setEditRecipientForm] = useState({
    id: '',
    name: '',
    phone: '',
    street: '',
    colonia: '',
    city: '',
    state: '',
    zipCode: ''
  });

  const mexicoStates = [
    'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche', 'Coahuila',
    'Colima', 'Chiapas', 'Chihuahua', 'Ciudad de México', 'Durango', 'Guanajuato',
    'Guerrero', 'Hidalgo', 'Jalisco', 'Estado de México', 'Michoacán', 'Morelos',
    'Nayarit', 'Nuevo León', 'Oaxaca', 'Puebla', 'Querétaro', 'Quintana Roo',
    'San Luis Potosí', 'Sinaloa', 'Sonora', 'Tabasco', 'Tamaulipas', 'Tlaxcala',
    'Veracruz', 'Yucatán', 'Zacatecas'
  ];

  useEffect(() => {
    if (user) {
      // Mantenerse en "Mi perfil" y poblar datos sin redirigir
      let prefix = '+52';
      let rawPhone = user.phone || '';
      const m = rawPhone.match(/^\+(\d{1,3})(.*)$/);
      if (m) {
        prefix = `+${m[1]}`;
        rawPhone = (m[2] || '').trim();
      }

      setPhonePrefix(prefix);
      setFormData({
        name: user.name || '',
        email: user.email || '',
        password: '',
        phone: rawPhone
      });
    }
  }, [user]);

  // Subscribe to Firestore orders for the authenticated user
  useEffect(() => {
    if (!user?.id) {
      setOrders([]);
      return;
    }

    setOrdersLoading(true);
    setOrdersError('');
    const q = query(
      collection(db, 'users', user.id, 'orders'),
      orderBy('createdAt', 'desc'),
      limit(20)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setOrders(list);
        setOrdersLoading(false);
      },
      (err) => {
        console.warn('[Profile] Error obteniendo pedidos', err);
        setOrdersError('No se pudieron cargar los pedidos');
        setOrdersLoading(false);
      }
    );
    return () => unsub();
  }, [user?.id]);

  const handleAuthSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        setSuccess('Inicio de sesión exitoso');
      } else {
        const fullPhone = `${phonePrefix}${formData.phone.replace(/\s+/g, '')}`;
        await register({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          phone: fullPhone
        });
        setSuccess('Registro exitoso');
      }
    } catch (err) {
      setError(isLogin ? 'Error al iniciar sesión' : 'Error al registrarse');
    }
  };

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const fullPhone = `${phonePrefix}${formData.phone.replace(/\s+/g, '')}`;
      await updateUser({ name: formData.name, phone: fullPhone });
      setSuccess('Perfil actualizado exitosamente');
    } catch (err) {
      setError('Error al actualizar el perfil');
    }
  };

  const handleAddAddress = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await addAddress({
        street: addressForm.street,
        colonia: addressForm.colonia,
        city: addressForm.city,
        state: addressForm.region,
        zipCode: addressForm.postalCode,
        isDefault: false
      });
      setAddressDialog(false);
      setAddressForm({ street: '', colonia: '', city: '', region: '', postalCode: '' });
      setSuccess('Dirección agregada exitosamente');
    } catch (err) {
      setError('Error al agregar la dirección');
    }
  };

  if (!user) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper 
          elevation={0} 
          sx={{ 
            p: { xs: 4, md: 5 }, 
            borderRadius: 4,
            boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
            backgroundColor: '#fff'
          }}
        >
          <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, color: '#333', mb: 3 }}>
            {isLogin ? 'Iniciar Sesión' : 'Registrarse'}
          </Typography>
          
          {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>{success}</Alert>}

          <form onSubmit={handleAuthSubmit}>
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                type="email"
                label="Email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              
              {!isLogin && (
                <>
                  <TextField
                    fullWidth
                    label="Nombre"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={4} sm={3}>
                      <TextField
                        fullWidth
                        label="Prefijo"
                        value={phonePrefix}
                        onChange={(e) => setPhonePrefix(e.target.value)}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={8} sm={9}>
                      <TextField
                        fullWidth
                        label="Teléfono"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                      />
                    </Grid>
                  </Grid>
                </>
              )}
              
              <TextField
                fullWidth
                type="password"
                label="Contraseña"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          sx={{
            backgroundColor: 'primary.main',
            '&:hover': { backgroundColor: 'primary.dark' },
            py: 1.5,
            borderRadius: '50px',
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: '0 4px 15px rgba(233, 30, 99, 0.3)'
          }}
        >
          {isLogin ? 'Iniciar Sesión' : 'Registrarse'}
        </Button>
          </form>

          <Box textAlign="center" sx={{ mt: 3 }}>
            <Button 
              onClick={() => setIsLogin(!isLogin)} 
              color="primary"
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '50px' }}
            >
              {isLogin ? '¿No tienes cuenta? Regístrate' : '¿Ya tienes cuenta? Inicia sesión'}
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 700, color: '#333', mb: 4 }}>
        Mi Perfil
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>{success}</Alert>}

      <Grid container spacing={4}>
        {/* Profile Information */}
        <Grid item xs={12} md={4}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 4, 
              borderRadius: 4,
              boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
              backgroundColor: '#fff',
              height: '100%'
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 600, mb: 3 }}>
              Información Personal
            </Typography>
            
            <form onSubmit={handleProfileUpdate}>
              <TextField
                fullWidth
                label="Nombre"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <TextField
                fullWidth
                type="email"
                label="Email"
                value={formData.email}
                disabled
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={4} sm={3}>
                  <TextField
                    fullWidth
                    label="Prefijo"
                    value={phonePrefix}
                    onChange={(e) => setPhonePrefix(e.target.value)}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={8} sm={9}>
                  <TextField
                    fullWidth
                    label="Teléfono"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  backgroundColor: 'primary.main',
                  '&:hover': { backgroundColor: 'primary.dark' },
                  borderRadius: '50px',
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 4px 15px rgba(233, 30, 99, 0.3)'
                }}
              >
                Actualizar Perfil
              </Button>
            </form>

            <Button
              fullWidth
              variant="outlined"
              onClick={logout}
              sx={{ 
                mt: 2, 
                color: 'primary.main', 
                borderColor: 'primary.main',
                borderRadius: '50px',
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Cerrar Sesión
            </Button>
          </Paper>
        </Grid>

        {/* Right column with tabs */}
        <Grid item xs={12} md={8}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 4, 
              borderRadius: 4,
              boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
              backgroundColor: '#fff',
              mb: 4
            }}
          >
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs
                value={tabIndex}
                onChange={(_, v) => setTabIndex(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ 
                  '& .MuiTab-root': { 
                    textTransform: 'none', 
                    fontWeight: 600, 
                    fontSize: '1rem',
                    fontFamily: '"Playfair Display", serif'
                  },
                  '& .Mui-selected': {
                    color: 'primary.main'
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: 'primary.main'
                  }
                }}
              >
                <Tab label="Mi Perfil" />
                <Tab label="Mis pedidos" />
                <Tab label="Mis Metodos de pago" />
              </Tabs>
            </Box>

            {/* Tab: Mi Perfil (addresses content) */}
            {tabIndex === 0 && (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}>
                    Mis Direcciones
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setAddressDialog(true)}
                    sx={{
                      backgroundColor: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.dark' },
                      borderRadius: '50px',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 3,
                      boxShadow: '0 4px 15px rgba(233, 30, 99, 0.3)'
                    }}
                  >
                    Agregar
                  </Button>
                </Box>

                {user.addresses && user.addresses.length > 0 ? (
                  <List>
                    {user.addresses.map((address) => (
                      <ListItem key={address.id} divider sx={{ px: 0 }}>
                        <ListItemText
                          primary={
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                {address.street}
                              </Typography>
                              {address.colonia && (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  {address.colonia}
                                </Typography>
                              )}
                              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                {address.city}, {address.state} {address.zipCode}
                              </Typography>
                              {address.isDefault && (
                                <Chip label="Predeterminada" size="small" color="primary" sx={{ mt: 1, borderRadius: 1 }} />
                              )}
                            </Box>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            onClick={() => deleteAddress(address.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
                    No tienes direcciones registradas
                  </Typography>
                )}
              </Box>
            )}

            {/* Tab: Mis pedidos */}
            {tabIndex === 1 && (
              <Box>
                <Typography variant="h6" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 600, mb: 3 }}>
                  Mis pedidos
                </Typography>
                {ordersError && (
                  <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{ordersError}</Alert>
                )}
                {ordersLoading ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Cargando pedidos...
                  </Typography>
                ) : orders.length === 0 ? (
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    No tienes pedidos registrados.
                  </Typography>
                ) : (
                  <List>
                    {orders.map((order: any) => {
                      const ts = order.createdAt?.toDate?.() || null;
                      const fecha = ts ? ts.toLocaleString() : '—';
                      const total = typeof order.total === 'number' ? order.total : 0;
                      const status = order.status || 'pendiente';
                      const itemsCount = Array.isArray(order.items) ? order.items.length : 0;
                      return (
                        <ListItem key={order.id} divider sx={{ px: 0 }}>
                          <ListItemText
                            primary={
                              <Box>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                  Pedido #{order.id}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  Fecha: {fecha}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                  Items: {itemsCount} • Total: ${total.toFixed(2)}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 1 }}>
                                <Chip label={status} size="small" color={status === 'paid' ? 'success' : 'default'} sx={{ borderRadius: 1 }} />
                              </Box>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => navigate(`/orders/${order.id}`)}
                              sx={{ borderRadius: '50px', textTransform: 'none', fontWeight: 600 }}
                            >
                              Ver más
                            </Button>
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Box>
            )}

            {/* Tab: Mis Metodos de pago */}
            {tabIndex === 2 && (
              <Box>
                <Typography variant="h6" sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 600, mb: 3 }}>
                  Mis Métodos de pago
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                  Aún no tienes métodos de pago guardados.
                </Typography>
                <Button
                  variant="outlined"
                  sx={{ 
                    color: 'primary.main', 
                    borderColor: 'primary.main',
                    borderRadius: '50px',
                    textTransform: 'none',
                    fontWeight: 600
                  }}
                >
                  Agregar método de pago
                </Button>
              </Box>
            )}
          </Paper>

          {/* Box: Mis Destinatarios */}
          <Paper 
            elevation={0} 
            sx={{ 
              p: 4, 
              borderRadius: 4,
              boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
              backgroundColor: '#fff'
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 600, mb: 3 }}>
              Mis Destinatarios
            </Typography>
            {user?.recipients && user.recipients.length > 0 ? (
              <List>
                {user.recipients.map((rec) => (
                  <ListItem key={rec.id} divider sx={{ px: 0 }}>
                    <ListItemText
                      primary={
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {rec.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {rec.phone ? `Tel: ${rec.phone}` : 'Tel: —'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {rec.street}{rec.colonia ? `, Col. ${rec.colonia}` : ''}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {rec.city}, {rec.state} {rec.zipCode}
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        aria-label="edit"
                        onClick={() => {
                          setEditRecipientForm({
                            id: rec.id,
                            name: rec.name || '',
                            phone: rec.phone || '',
                            street: rec.street || '',
                            colonia: rec.colonia || '',
                            city: rec.city || '',
                            state: rec.state || '',
                            zipCode: rec.zipCode || ''
                          });
                          setOpenEditRecipientDialog(true);
                        }}
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={async () => {
                          const ok = window.confirm('¿Eliminar este destinatario?');
                          if (ok) {
                            try {
                              await deleteRecipient(rec.id);
                            } catch (e) {
                              console.error(e);
                              alert('No se pudo eliminar el destinatario');
                            }
                          }
                        }}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
                Aún no tienes destinatarios guardados.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Address Dialog */}
      <Dialog open={addressDialog} onClose={() => setAddressDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}>Agregar Nueva Dirección</DialogTitle>
        <form onSubmit={handleAddAddress}>
          <DialogContent>
            <TextField
              fullWidth
              label="Calle"
              value={addressForm.street}
              onChange={(e) => setAddressForm({ ...addressForm, street: e.target.value })}
              required
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              fullWidth
              label="Colonia"
              value={addressForm.colonia}
              onChange={(e) => setAddressForm({ ...addressForm, colonia: e.target.value })}
              required
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              fullWidth
              label="Ciudad"
              value={addressForm.city}
              onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
              required
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              fullWidth
              select
              label="Estado"
              value={addressForm.region}
              onChange={(e) => setAddressForm({ ...addressForm, region: e.target.value })}
              required
              sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              {mexicoStates.map((st) => (
                <MenuItem key={st} value={st}>{st}</MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              label="Código Postal"
              value={addressForm.postalCode}
              onChange={(e) => setAddressForm({ ...addressForm, postalCode: e.target.value })}
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setAddressDialog(false)} sx={{ borderRadius: '50px', textTransform: 'none' }}>Cancelar</Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                backgroundColor: 'primary.main',
                '&:hover': { backgroundColor: 'primary.dark' },
                borderRadius: '50px',
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                boxShadow: '0 4px 15px rgba(233, 30, 99, 0.3)'
              }}
            >
              Agregar Dirección
            </Button>
          </DialogActions>
        </form>
  </Dialog>
  {/* Edit Recipient Dialog */}
  <Dialog open={openEditRecipientDialog} onClose={() => setOpenEditRecipientDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
    <DialogTitle sx={{ fontFamily: '"Playfair Display", serif', fontWeight: 600 }}>Editar Destinatario</DialogTitle>
    <DialogContent>
      <TextField
        fullWidth
        label="Nombre"
        value={editRecipientForm.name}
        onChange={(e) => setEditRecipientForm({ ...editRecipientForm, name: e.target.value })}
        sx={{ mb: 2, mt: 1, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
      />
      <TextField
        fullWidth
        label="Teléfono"
        value={editRecipientForm.phone}
        onChange={(e) => setEditRecipientForm({ ...editRecipientForm, phone: e.target.value })}
        sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
      />
      <TextField
        fullWidth
        label="Calle"
        value={editRecipientForm.street}
        onChange={(e) => setEditRecipientForm({ ...editRecipientForm, street: e.target.value })}
        sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
      />
      <TextField
        fullWidth
        label="Colonia"
        value={editRecipientForm.colonia}
        onChange={(e) => setEditRecipientForm({ ...editRecipientForm, colonia: e.target.value })}
        sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
      />
      <TextField
        fullWidth
        label="Ciudad"
        value={editRecipientForm.city}
        onChange={(e) => setEditRecipientForm({ ...editRecipientForm, city: e.target.value })}
        sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
      />
      <TextField
        fullWidth
        select
        label="Estado"
        value={editRecipientForm.state}
        onChange={(e) => setEditRecipientForm({ ...editRecipientForm, state: e.target.value })}
        sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
      >
        {mexicoStates.map((st) => (
          <MenuItem key={st} value={st}>{st}</MenuItem>
        ))}
      </TextField>
      <TextField
        fullWidth
        label="Código Postal"
        value={editRecipientForm.zipCode}
        onChange={(e) => setEditRecipientForm({ ...editRecipientForm, zipCode: e.target.value })}
        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
      />
    </DialogContent>
    <DialogActions sx={{ p: 2 }}>
      <Button onClick={() => setOpenEditRecipientDialog(false)} sx={{ borderRadius: '50px', textTransform: 'none' }}>Cancelar</Button>
      <Button
        variant="contained"
        onClick={async () => {
          try {
            const { id, name, phone, street, colonia, city, state, zipCode } = editRecipientForm;
            await updateRecipient(id, { name, phone, street, colonia, city, state, zipCode });
            setOpenEditRecipientDialog(false);
          } catch (e) {
            console.error(e);
            alert('No se pudo actualizar el destinatario');
          }
        }}
        sx={{ 
          backgroundColor: 'primary.main', 
          '&:hover': { backgroundColor: 'primary.dark' },
          borderRadius: '50px',
          textTransform: 'none',
          fontWeight: 600,
          px: 3,
          boxShadow: '0 4px 15px rgba(233, 30, 99, 0.3)'
        }}
      >
        Guardar cambios
      </Button>
    </DialogActions>
  </Dialog>
    </Container>
  );
};

export default ProfilePage;